import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { StatsProvider } from "@/context/StatsContext";
import { SettingsProvider } from "@/context/SettingsContext";
import IdleGuard from "@/components/IdleGuard";
import LangSync from "@/components/LangSync";
import { LangProvider } from "@/i18n";
import Login from "@/components/auth/Login";
import ResetPassword from "@/pages/ResetPassword";
import DemoLanding from "@/pages/DemoLanding";
import AppLayout from "@/components/layout/AppLayout";
import Dashboard from "@/components/dashboard/Dashboard";
import Aankomend from "@/pages/Aankomend";
import Aankomsten from "@/pages/Aankomsten";
import Reservaties from "@/pages/Reservaties";
import Vertrekkend from "@/pages/Vertrekkend";
import Afrekening from "@/pages/Afrekening";
import Verhuur from "@/pages/Verhuur";
import Verhuizen from "@/pages/Verhuizen";
import Verbeteringen from "@/pages/Verbeteringen";
import Gasten from "@/pages/Gasten";
import LosseVerkopen from "@/pages/LosseVerkopen";
import Facturen from "@/pages/Facturen";
import Jobs from "@/pages/Jobs";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminCamping from "@/pages/admin/AdminCamping";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminMap from "@/pages/admin/AdminMap";
import AdminInspection from "@/pages/admin/AdminInspection";
import AdminMaintenance from "@/pages/admin/AdminMaintenance";
import AdminMenu from "@/pages/admin/AdminMenu";
import AdminStock from "@/pages/admin/AdminStock";
import AdminPurchases from "@/pages/admin/AdminPurchases";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminRentals from "@/pages/admin/AdminRentals";
import AdminRevenue from "@/pages/admin/AdminRevenue";
import AdminSellerRevenue from "@/pages/admin/AdminSellerRevenue";
import AdminSeasonReport from "@/pages/admin/AdminSeasonReport";
import AdminDeposits from "@/pages/admin/AdminDeposits";
import AdminTariffs from "@/pages/admin/AdminTariffs";
import AdminSalesInvoices from "@/pages/admin/AdminSalesInvoices";
import AdminCashClose from "@/pages/admin/AdminCashClose";
import AdminPrepayments from "@/pages/admin/AdminPrepayments";
import AdminVat from "@/pages/admin/AdminVat";
import AdminExpenses from "@/pages/admin/AdminExpenses";
import AdminEmail from "@/pages/admin/AdminEmail";
import AdminTenants from "@/pages/admin/AdminTenants";
import AdminInterfaces from "@/pages/admin/AdminInterfaces";
import AdminLoginLog from "@/pages/admin/AdminLoginLog";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import Mailbox from "@/pages/Mailbox";
import InterfacesOverview from "@/pages/InterfacesOverview";
import Gegevens from "@/pages/Gegevens";
import AdminReviews from "@/pages/admin/AdminReviews";
import Beoordeling from "@/pages/Beoordeling";
import Gastenboek from "@/pages/Gastenboek";
import Timesheets from "@/pages/Timesheets";
import PrintCashClose from "@/pages/PrintCashClose";
import PrintCashDiff from "@/pages/PrintCashDiff";
import PrintDeposit from "@/pages/PrintDeposit";
import PrintInvoice from "@/pages/PrintInvoice";
import PrintDunning from "@/pages/PrintDunning";
import KaartUpdate from "@/pages/KaartUpdate";
import PrintPage from "@/pages/PrintPage";
import PlanningPrint from "@/pages/PlanningPrint";
import PrintSale from "@/pages/PrintSale";
import ErrorBoundary from "@/components/ErrorBoundary";
import Wachtwoord from "@/pages/Wachtwoord";
import Verblijven from "@/pages/Verblijven";
import BetalingOk from "@/pages/BetalingOk";
import BetalingGeannuleerd from "@/pages/BetalingGeannuleerd";
import Display from "@/pages/Display";

function ProtectedRoute() {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center cb-sky-gradient">
        <div className="animate-pulse text-primary font-display text-lg">CampAd Pro…</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password && location.pathname !== "/wachtwoord") return <Navigate to="/wachtwoord" replace />;
  return <Outlet />;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <LangProvider>
        <AuthProvider>
         <SettingsProvider>
         <StatsProvider>
          <IdleGuard />
          <LangSync />
          <Toaster position="top-right" richColors />
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/herstel-wachtwoord" element={<ResetPassword />} />
              <Route path="/demo" element={<DemoLanding />} />
              <Route path="/beoordeling/:token" element={<Beoordeling />} />
              <Route path="/gegevens/:token" element={<Gegevens />} />
              <Route path="/gastenboek" element={<Gastenboek />} />
              <Route path="/display/:tenant" element={<Display />} />
              <Route path="/betaling-ok" element={<BetalingOk />} />
              <Route path="/betaling-geannuleerd" element={<BetalingGeannuleerd />} />
              <Route element={<ProtectedRoute />}>
                <Route path="/print" element={<PrintPage />} />
                <Route path="/print-planning" element={<PlanningPrint />} />
                <Route path="/print-verkoop" element={<PrintSale />} />
                <Route path="/print-dagafsluiting" element={<PrintCashClose />} />
                <Route path="/print-kasverschil" element={<PrintCashDiff />} />
                <Route path="/print-afstorting" element={<PrintDeposit />} />
                <Route path="/print-factuur" element={<PrintInvoice />} />
                <Route path="/print-aanmaningen" element={<PrintDunning />} />
                <Route element={<AppLayout />}>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/interfaces" element={<InterfacesOverview />} />
                  <Route path="/aankomend" element={<Aankomend />} />
                  <Route path="/aankomsten" element={<Aankomsten />} />
                  <Route path="/reservaties" element={<Reservaties />} />
                  <Route path="/verblijven" element={<Verblijven />} />
                  <Route path="/vertrekkend" element={<Vertrekkend />} />
                  <Route path="/afrekening" element={<Afrekening />} />
                  <Route path="/verhuur" element={<Verhuur />} />
                  <Route path="/verhuizen" element={<Verhuizen />} />
                  <Route path="/verbeteringen" element={<Verbeteringen />} />
                  <Route path="/gasten" element={<Gasten />} />
                  <Route path="/verkopen" element={<LosseVerkopen />} />
                  <Route path="/facturen" element={<Facturen />} />
                  <Route path="/jobs" element={<Jobs />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<Navigate to="/admin/camping" replace />} />
                    <Route path="camping" element={<AdminCamping />} />
                    <Route path="gebruikers" element={<AdminUsers />} />
                    <Route path="plattegrond" element={<AdminMap />} />
                    <Route path="controle" element={<AdminInspection />} />
                    <Route path="onderhoud" element={<AdminMaintenance />} />
                    <Route path="menu" element={<AdminMenu />} />
                    <Route path="voorraad" element={<AdminStock />} />
                    <Route path="tarieven" element={<AdminTariffs />} />
                    <Route path="verhuur" element={<AdminRentals />} />
                    <Route path="omzet" element={<AdminRevenue />} />
                    <Route path="omzet-medewerker" element={<AdminSellerRevenue />} />
                    <Route path="afstortingen" element={<AdminDeposits />} />
                    <Route path="jaaroverzicht" element={<AdminSeasonReport />} />
                    <Route path="leveranciers" element={<AdminSuppliers />} />
                    <Route path="inkomende-facturen" element={<AdminPurchases />} />
                    <Route path="uitgaande-facturen" element={<AdminSalesInvoices />} />
                    <Route path="dagafsluiting" element={<AdminCashClose />} />
                    <Route path="voorschotten" element={<AdminPrepayments />} />
                    <Route path="btw-overzicht" element={<AdminVat />} />
                    <Route path="kosten" element={<AdminExpenses />} />
                    <Route path="gastenboek" element={<AdminReviews />} />
                    <Route path="mailserver" element={<AdminEmail />} />
                    <Route path="uren" element={<Timesheets />} />
                    <Route path="campings" element={<AdminTenants />} />
                    <Route path="interfaces" element={<AdminInterfaces />} />
                    <Route path="interfaces/:iface" element={<AdminInterfaces />} />
                    <Route path="aanmeldlog" element={<AdminLoginLog />} />
                    <Route path="logboek" element={<AdminAuditLog />} />
                  </Route>
                  <Route path="/admin/kaart" element={<KaartUpdate />} />
                  <Route path="/uren" element={<Timesheets />} />
                  <Route path="/mailbox" element={<Mailbox />} />
                  <Route path="/wachtwoord" element={<Wachtwoord />} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
         </StatsProvider>
         </SettingsProvider>
        </AuthProvider>
      </LangProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
